import { useEffect, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import Login from './pages/Login';
import Plan from './pages/Plan';
import PlanHistory from './pages/PlanHistory';
import PastWeek from './pages/PastWeek';
import Recipes from './pages/Recipes';
import RecipeDetail from './pages/RecipeDetail';
import RecipeCard from './pages/RecipeCard';
import ShoppingList from './pages/ShoppingList';
import CartReview from './pages/CartReview';
import Pantry from './pages/Pantry';
import Profile from './pages/Profile';
import Review from './pages/Review';
import CardReview from './pages/review/CardReview';
import MatchReview from './pages/review/MatchReview';
import StoreCoverage from './pages/review/StoreCoverage';

const TABS = [
  { to: '/', label: 'Plan', icon: '🍽️', end: true },
  { to: '/list', label: 'List', icon: '🧾', end: false },
  { to: '/recipes', label: 'Recipes', icon: '📖', end: false },
  { to: '/pantry', label: 'Pantry', icon: '🫙', end: false },
  { to: '/profile', label: 'Profile', icon: '⚙️', end: false },
];

/** Start a new page at the top of it.
 *
 * A router swaps the markup and leaves the scroll position alone, so opening a
 * recipe from halfway down the corpus list dropped you halfway down the recipe.
 * Keyed on pathname rather than the whole location: the shopping list rewrites
 * its own query string as items are made at home, and that is the same page.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

export default function App() {
  const { user, loading } = useAuth();
  const online = useOnline();

  if (loading) return <p className="center-note">Loading…</p>;
  if (!user?.authenticated) return <Login />;

  return (
    <div className="app">
      {!online && (
        <div className="offline-banner">Offline — showing your saved plan and list.</div>
      )}
      <ScrollToTop />
      <main className="app__main">
        <Routes>
          <Route path="/" element={<Plan />} />
          <Route path="/list" element={<ShoppingList />} />
          {/* A look at the basket before anything reaches the store. */}
          <Route path="/list/review" element={<CartReview />} />
          {/* Reached from the plan rather than the bottom nav: five tabs is
              already the width of a thumb, and looking back at what you ate
              is not something done in an aisle. */}
          <Route path="/history" element={<PlanHistory />} />
          <Route path="/history/:week" element={<PastWeek />} />
          <Route path="/recipes" element={<Recipes />} />
          <Route path="/recipes/:id" element={<RecipeDetail />} />
          {/* The printed card, in a page of our own. Opening the PDF in a new
              tab strands you: an installed PWA has no chrome to go back with. */}
          <Route path="/recipes/:id/card" element={<RecipeCard />} />
          <Route path="/pantry" element={<Pantry />} />
          <Route path="/profile" element={<Profile />} />
          {/* Bookmarks and the home-screen shortcut predate the rename. */}
          <Route path="/settings" element={<Navigate to="/profile" replace />} />
          {/* Reached from Profile rather than the bottom nav: checking
              extraction is desk work, and the nav is the in-store surface. */}
          <Route path="/review" element={<Review />} />
          <Route path="/review/cards" element={<CardReview />} />
          <Route path="/review/matches" element={<MatchReview />} />
          <Route path="/review/stores" element={<StoreCoverage />} />
          <Route path="*" element={<p className="center-note">Not found.</p>} />
        </Routes>
      </main>
      <nav className="nav">
        {TABS.map((tab) => (
          <NavLink key={tab.to} to={tab.to} end={tab.end} className="nav__link">
            <span className="nav__icon" aria-hidden="true">
              {tab.icon}
            </span>
            <span>{tab.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
